import React, { useEffect } from "react";
import { useRouter } from "next/router";
import { useCommonContext } from "context/CommonDataContext";
import ManageBookingContextProvider from "context/ManageBookingContext";
import { GetStaticPropsArgs, PageProps } from "pages";
import { replaceAllSbDomainsWithAkasa } from "@/utils/akasaDomainConverters/replaceSbDomainWithAkasa";
import { redirectToDarkSite } from "@/utils/redirectToDarkSite/redirectToDarkSite";
import { useDispatch } from "react-redux";
import { resetAffiliateId, setAffiliateId, setAffiliateImpressionId, setAffiliateRedirectId } from "@/store/slices/booking";
import { toast } from "react-toastify";
import CustomStoryBlockComponent from "../components/CustomStoryBlockComponent";
import Layout from "../components/Layout";
import PageNotFound from "../components/common/PageNotFound";
import { configPages } from "../config/configPages";
import { AuthContextProvider, useAuth } from "../context/AuthContext";
import Storyblok, { useStoryblok } from "../utils/storyblok";
import { saveAffiliatesImpression } from "../dataLayer/Affiliates/Affiliates";
import { AFFILIATE_STAGE } from "../constants/variables";
import "react-toastify/dist/ReactToastify.min.css";

export type PathParams = {
  slug: string[];
};

toast.configure();

export default function Page({ story, navFooter, locale, resources, cookieData, redirectForCurrentRoute }: PageProps) {
  const enableBridge = true;
  const sbStory = useStoryblok(story, enableBridge, locale);
  const { signOut } = useAuth();
  const router = useRouter();
  const { webConfig } = useCommonContext();
  const dispatch = useDispatch();

  useEffect(() => {
    const urlParameters: any = router.query;
    const utmAffiliate = urlParameters?.utm_affiliate;
    const search = urlParameters?.search;
    const redirectId = urlParameters?.skyscanner_redirectid;
    const campaign = urlParameters?.campaign;
    const medium = urlParameters?.medium;
    const stage = search === "true" ? AFFILIATE_STAGE.DAYVIEW_PAGE : AFFILIATE_STAGE.HOME_PAGE

    const affiliateId = sessionStorage.getItem("affiliateId");
    if (utmAffiliate && utmAffiliate !== affiliateId) {
      // set the utm in redux for future use;
      sessionStorage.setItem("affiliateId", utmAffiliate);
      dispatch(setAffiliateId({ affiliateId: utmAffiliate }));
      dispatch(setAffiliateRedirectId({ redirectId }));

      // call api if utm_affiliate present to save in backend
      const timestamp = Date.now();
      saveAffiliatesImpression({
        fullUrlRequested: router.asPath, 
        timestamp, 
        affiliateId: utmAffiliate,
        redirectId,
        campaign,
        medium,
        stage})
        .then((data) => {
          dispatch(setAffiliateImpressionId({ affiliateImpressionId: data.impressionId }));
          sessionStorage.setItem("affiliateSourceOrgCode", data?.orgCode);
        });
    } else if (!affiliateId) {
      // Reset affliate id from booking redux if the session has expired
      dispatch(resetAffiliateId());
    }
  }, [router.query]);

  useEffect(() => {
    const currentPath = router.asPath;

    if (webConfig?.enableDarkSite) {
      redirectToDarkSite();
      return;
    }

    if (currentPath === "/?signout") {
      (async () => {
        await signOut()
          .then((response: any) => {
            if (response) {
              router.push("/");
            }

            return null;
          })
          .catch((error: unknown) => {
            console.warn(error);
          });
      })();
    }
  }, [redirectForCurrentRoute, router, signOut, webConfig?.enableDarkSite]);

  const seoData = {
    meta: sbStory?.content?.meta,
    custom_tag: sbStory?.content?.custom_tag,
    canonical: sbStory?.content?.canonical,
    keywords: sbStory?.content?.keywords,
    language: sbStory?.content?.language,
    robots: sbStory?.content?.robots,
    schema: sbStory?.content?.schema
  };

  const navConfig = {
    remove_footer: sbStory?.content?.remove_footer,
    remove_navbar: sbStory?.content?.remove_navbar,
    transparent_navbar: sbStory?.content?.transparant_navbar,
    remove_mob_footer: sbStory?.content?.remove_mob_footer,
    remove_mob_navbar: sbStory?.content?.remove_mob_navbar,
    remove_subscribe: sbStory?.content?.remove_subscribe,
    remove_mob_subscribe: sbStory?.content?.remove_mob_subscribe,
    show_marquee: sbStory?.content?.show_marquee
  };

  const storyWithNewAssetDomainName = replaceAllSbDomainsWithAkasa({ dataToUpdate: sbStory?.content });
  const headerAndFooterDataWithNewAssetDomainName = replaceAllSbDomainsWithAkasa({ dataToUpdate: navFooter });

  if (sbStory?.content) {
    return (
      <AuthContextProvider>
        <ManageBookingContextProvider>
            <Layout
              seoData={seoData}
              navFooter={headerAndFooterDataWithNewAssetDomainName}
              resources={resources}
              navConfig={navConfig}
              cookieData={cookieData}
            >
              <CustomStoryBlockComponent blok={storyWithNewAssetDomainName} resources={resources} />
            </Layout>
        </ManageBookingContextProvider>
      </AuthContextProvider>
    );
  }
  return <PageNotFound />;
}

export async function getStaticProps({
  locale,
  locales,
  defaultLocale,
  params,
  preview = false,
  previewData
}: GetStaticPropsArgs & { params: PathParams }) {
  const slug = params.slug ? params.slug.join("/") : "home";
  const version = Date.now();

  const sbParams = {
    version: "published",
    resolve_relations: ["featured-posts.posts", "selected-posts.posts"],
    language: locale,
    cv: version,
    from_release: ""
  };

  if (preview) {
    sbParams.version = preview ? "draft" : "published";
    sbParams.cv = version;
    sbParams.from_release = previewData.releaseId;
  }

  let response;
  try {
    response = await Storyblok.get(`cdn/stories/${slug}`, sbParams);
  } catch (error) {
    console.warn(error);
  }

  const data = response?.data;
  const navData = await Storyblok.get(`cdn/stories/nav-and-footer `, sbParams);
  const resources = await Storyblok.get(`cdn/stories/content/app-master-data `, sbParams);
  const allPages = await Storyblok.get(`cdn/stories/mob/all-pages `, sbParams);
  const webConfig = allPages?.data?.story?.content?.webConfig?.[0];
  const isPageDisabled = data?.story?.content?.isPageDisabled;

  let notFound = true;

  if (typeof data !== "undefined") {
    if (data?.story?.content && !isPageDisabled) {
      notFound = false;
    }
  }

  return {
    props: {
      story: data ? data.story : false,
      navFooter: navData ? navData.data.story.content : false,
      preview,
      locale,
      locales,
      defaultLocale,
      resources: resources ? resources.data.story?.content : null,
      cookieData: allPages?.data?.story?.content?.cookie_policy_bar?.[0],
      slug,
      webConfig
    },
    notFound,
    revalidate: 10
  };
}

export async function getStaticPaths() {
  const { data } = await Storyblok.get("cdn/links/");

  const paths: { params: PathParams }[] = [];

  Object.keys(data.links).forEach((linkKey) => {
    if (data.links[linkKey].is_folder || data.links[linkKey].slug === "home") {
      return;
    }

    if (!configPages.pages.includes(data.links[linkKey].slug)) {
      const { slug } = data.links[linkKey];
      const splitSlug = slug.split("/");
      paths.push({ params: { slug: splitSlug } });
    }
  });

  return {
    paths,
    fallback: "blocking"
  };
}